import { useLingui } from '@lingui/react/macro';
import { z } from 'zod';

import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { resolvePatternText, textSchema } from '@/shared/patterns';

import type { AmortizationResult } from './amortization';
import { AmortizationSchedule } from './amortization-schedule';
import { AMORTIZATION_SUMMARY_FIELDS, AmortizationSummary, type AmortizationSummaryField } from './amortization-summary';

const summaryField = z.enum(AMORTIZATION_SUMMARY_FIELDS);

/** Props do resumo como genérico de bloco: visibilidade e rótulo por tenant, prazo em meses, id da simulação. */
export const amortizationSummaryBlockProps = z.object({
  fields: z.partialRecord(summaryField, z.boolean()).optional(),
  labels: z.partialRecord(summaryField, textSchema).optional(),
  termInMonths: z.boolean().optional(),
  showSimulationId: z.boolean().optional(),
});
export type AmortizationSummaryBlockProps = z.infer<typeof amortizationSummaryBlockProps>;

/** Props do plano de pagamento como genérico de bloco. */
export const amortizationScheduleBlockProps = z.object({
  // Em edição manual do plano, não o `customPaymentPlan` salvo da operação (SPEC, R1 do !11135).
  customPaymentPlan: z.boolean().optional(),
  fixedCosts: z.boolean().optional(),
  fixedCostsLabel: textSchema.optional(),
});
export type AmortizationScheduleBlockProps = z.infer<typeof amortizationScheduleBlockProps>;

type SummaryView = BlockComponentProps<AmortizationSummaryBlockProps, AmortizationResult>;
type ScheduleView = BlockComponentProps<AmortizationScheduleBlockProps, AmortizationResult>;

function useSummaryProps({ props, data }: SummaryView) {
  const { i18n } = useLingui();
  const labels: Partial<Record<AmortizationSummaryField, string>> = {};
  for (const id of AMORTIZATION_SUMMARY_FIELDS) {
    const text = props.labels?.[id];
    if (text !== undefined) labels[id] = resolvePatternText(i18n, text);
  }
  return {
    result: data,
    labels,
    ...(props.fields ? { fields: props.fields } : {}),
    ...(props.termInMonths ? { termInMonths: true } : {}),
    ...(props.showSimulationId ? { simulationId: data.id } : {}),
  };
}

function SummaryWeb(view: SummaryView) {
  return <AmortizationSummary {...useSummaryProps(view)} presentation="web" />;
}

function SummaryMobile(view: SummaryView) {
  return <AmortizationSummary {...useSummaryProps(view)} presentation="mobile" />;
}

function useScheduleProps({ props, data }: ScheduleView) {
  const { i18n } = useLingui();
  return {
    items: data.paymentScheduleItems,
    indexer: data,
    ...(props.customPaymentPlan ? { customPaymentPlan: true } : {}),
    ...(props.fixedCosts ? { fixedCosts: true } : {}),
    ...(props.fixedCostsLabel ? { fixedCostsLabel: resolvePatternText(i18n, props.fixedCostsLabel) } : {}),
  };
}

function ScheduleWeb(view: ScheduleView) {
  return <AmortizationSchedule {...useScheduleProps(view)} presentation="web" />;
}

function ScheduleMobile(view: ScheduleView) {
  return <AmortizationSchedule {...useScheduleProps(view)} presentation="mobile" />;
}

/** "Resultados da operação" como genérico do catálogo, sobre uma fonte `record` com o resultado da amortização. */
export const AmortizationSummaryBlock = defineBlockComponent<AmortizationSummaryBlockProps, AmortizationResult>({
  id: 'AmortizationSummary',
  description: 'Results of one amortization (issue and due dates, term, indexer, contract, net and future values, IOF, rates and effective cost), formatted as the legacy form.',
  props: amortizationSummaryBlockProps,
  render: { web: SummaryWeb, mobile: SummaryMobile },
});

/** Plano de pagamento como genérico do catálogo, sobre uma fonte `record` com `paymentScheduleItems`. */
export const AmortizationScheduleBlock = defineBlockComponent<AmortizationScheduleBlockProps, AmortizationResult>({
  id: 'AmortizationSchedule',
  description: 'Payment schedule of one amortization: installment, due date, balance, amortization, interest, optional IOF and fees, payment and the total.',
  props: amortizationScheduleBlockProps,
  render: { web: ScheduleWeb, mobile: ScheduleMobile },
});
